const React = require('react');
const { createElement: h, useState, useEffect, useRef } = React;
const { request } = require('../lib/apiClient');
require('./GoogleScopePickerModal.css');

// Two-panel popup (NOT a native popup) shown BEFORE the Google OAuth window.
// Left: a dark trust rail explaining the "unverified app" warning the user will
// see (Alleflow is mid-verification). Right: the form — connection name + a
// per-service permission picker (each service collapses and has a parent
// select-all checkbox), a global Select all, and a live "N selected" footer.
//
// The name is REQUIRED — it's part of the credential identity, so the same
// Google account can be connected more than once with different permissions.
// At least one scope must be selected.
//
//   onConfirm({ name, scopes }): proceed to open the Google OAuth window
//   onCancel():                  dismiss without connecting
//
// The scope catalog comes from the backend (GET /google-auth/scopes) so it
// stays the single source of truth.

// ---- Inline service icons (self-contained, no external requests) ----------
function iconWrap(tone, child) {
  return h('span', { className: 'gsp-svc-icon gsp-svc-icon--' + tone }, child);
}
var SERVICE_ICONS = {
  gmail: function() {
    return iconWrap('gmail', h('svg', { viewBox: '0 0 24 24', width: 18, height: 18, fill: 'none' },
      h('rect', { x: 2.5, y: 5, width: 19, height: 14, rx: 2, stroke: 'currentColor', strokeWidth: 1.8 }),
      h('path', { d: 'M3.5 6.5 12 13l8.5-6.5', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' })
    ));
  },
  calendar: function() {
    return iconWrap('calendar', h('svg', { viewBox: '0 0 24 24', width: 18, height: 18, fill: 'none' },
      h('rect', { x: 3.5, y: 5, width: 17, height: 15, rx: 2, stroke: 'currentColor', strokeWidth: 1.8 }),
      h('path', { d: 'M3.5 9h17M8 3.5v3M16 3.5v3', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' })
    ));
  },
  drive: function() {
    return iconWrap('drive', h('svg', { viewBox: '0 0 24 24', width: 18, height: 18, fill: 'none' },
      h('path', { d: 'M9 4h6l6 10-3 5H6l-3-5L9 4Z', stroke: 'currentColor', strokeWidth: 1.8, strokeLinejoin: 'round' }),
      h('path', { d: 'M9 4l6 10M15 14H3M15 14l3 5', stroke: 'currentColor', strokeWidth: 1.8, strokeLinejoin: 'round' })
    ));
  },
  sheets: function() {
    return iconWrap('sheets', h('svg', { viewBox: '0 0 24 24', width: 18, height: 18, fill: 'none' },
      h('rect', { x: 4, y: 3.5, width: 16, height: 17, rx: 2, stroke: 'currentColor', strokeWidth: 1.8 }),
      h('path', { d: 'M4 9h16M4 14h16M9.5 9v11.5M14.5 9v11.5', stroke: 'currentColor', strokeWidth: 1.6 })
    ));
  },
  docs: function() {
    return iconWrap('docs', h('svg', { viewBox: '0 0 24 24', width: 18, height: 18, fill: 'none' },
      h('path', { d: 'M6 3.5h8l4 4v13H6V3.5Z', stroke: 'currentColor', strokeWidth: 1.8, strokeLinejoin: 'round' }),
      h('path', { d: 'M14 3.5V8h4M8.5 12h7M8.5 15.5h7', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round' })
    ));
  }
};
function serviceIcon(key) {
  var fn = SERVICE_ICONS[key];
  return fn ? fn() : iconWrap('docs', null);
}

function groupScopes(group) {
  return (group.roles || []).map(function(role) { return role.scope; });
}

// Checkbox that can render the indeterminate (dash) state — a DOM property
// React can't set via props, so we set it on the ref.
function TriStateCheckbox(props) {
  var ref = useRef(null);
  useEffect(function() {
    if (ref.current) ref.current.indeterminate = !!props.indeterminate;
  }, [props.indeterminate, props.checked]);
  return h('input', {
    type: 'checkbox',
    className: props.className,
    checked: !!props.checked,
    ref: ref,
    onChange: props.onChange,
    onClick: props.onClick
  });
}

function GoogleScopePickerModal(props) {
  var onConfirm = props.onConfirm || function() {};
  var onCancel = props.onCancel || function() {};

  var [groups, setGroups] = useState([]);
  var [loadError, setLoadError] = useState('');
  var [name, setName] = useState('');
  var [selected, setSelected] = useState({});   // set of selected scope strings
  var [expanded, setExpanded] = useState({});    // set of expanded group keys
  var [submitted, setSubmitted] = useState(false);

  useEffect(function() {
    var cancelled = false;
    request('/google-auth/scopes')
      .then(function(result) {
        if (cancelled) return;
        var list = (result && Array.isArray(result.groups)) ? result.groups : [];
        setGroups(list);
        var open = {};
        list.forEach(function(g) { open[g.key] = true; });
        setExpanded(open);
      })
      .catch(function(error) {
        if (cancelled) return;
        setLoadError(error && error.message ? error.message : 'Could not load permissions.');
      });
    return function() { cancelled = true; };
  }, []);

  useEffect(function() {
    function onKeyDown(e) { if (e.key === 'Escape') onCancel(); }
    document.addEventListener('keydown', onKeyDown);
    return function() { document.removeEventListener('keydown', onKeyDown); };
  }, []);

  // ---- selection helpers ----------------------------------------------------
  var allScopes = groups.reduce(function(acc, g) { return acc.concat(groupScopes(g)); }, []);
  var selectedScopes = Object.keys(selected);
  var allOn = allScopes.length > 0 && allScopes.every(function(s) { return selected[s]; });

  function toggleScope(scope) {
    setSelected(function(prev) {
      var next = Object.assign({}, prev);
      if (next[scope]) { delete next[scope]; } else { next[scope] = true; }
      return next;
    });
  }

  function toggleExpanded(key) {
    setExpanded(function(prev) {
      var next = Object.assign({}, prev);
      next[key] = !next[key];
      return next;
    });
  }

  function toggleGroup(group) {
    var scopes = groupScopes(group);
    var groupAllOn = scopes.every(function(s) { return selected[s]; });
    setSelected(function(prev) {
      var next = Object.assign({}, prev);
      scopes.forEach(function(s) { if (groupAllOn) { delete next[s]; } else { next[s] = true; } });
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected(function() {
      if (allOn) return {};
      var next = {};
      allScopes.forEach(function(s) { next[s] = true; });
      return next;
    });
  }

  function groupState(group) {
    var scopes = groupScopes(group);
    var on = scopes.filter(function(s) { return selected[s]; }).length;
    return { all: on > 0 && on === scopes.length, some: on > 0 && on < scopes.length, count: on };
  }

  var nameTrimmed = name.trim();
  var nameMissing = !nameTrimmed;
  var noScopes = selectedScopes.length === 0;

  function submit() {
    setSubmitted(true);
    if (nameMissing || noScopes) return;
    onConfirm({ name: nameTrimmed, scopes: selectedScopes });
  }

  // ---- render ---------------------------------------------------------------
  function renderRole(role) {
    var scope = role.scope;
    var checked = !!selected[scope];
    return h('label', { key: scope, className: 'gsp-role' + (checked ? ' gsp-role--on' : '') },
      h('input', {
        type: 'checkbox',
        className: 'gsp-check gsp-check--role',
        checked: checked,
        onChange: function() { toggleScope(scope); }
      }),
      h('span', { className: 'gsp-role-text' },
        h('span', { className: 'gsp-role-title' }, role.title),
        h('span', { className: 'gsp-role-desc' }, role.description)
      )
    );
  }

  function renderGroup(group) {
    var state = groupState(group);
    var isOpen = !!expanded[group.key];
    var count = (group.roles || []).length;
    return h('div', { key: group.key, className: 'gsp-card' + (isOpen ? ' gsp-card--open' : '') },
      h('div', { className: 'gsp-card-head' },
        h(TriStateCheckbox, {
          className: 'gsp-check gsp-check--group',
          checked: state.all,
          indeterminate: state.some,
          onChange: function() { toggleGroup(group); },
          onClick: function(e) { e.stopPropagation(); }
        }),
        serviceIcon(group.key),
        h('button', { type: 'button', className: 'gsp-card-toggle', onClick: function() { toggleExpanded(group.key); } },
          h('span', { className: 'gsp-card-titles' },
            h('span', { className: 'gsp-card-label' }, group.label),
            h('span', { className: 'gsp-card-meta' },
              state.count > 0 ? (state.count + ' of ' + count + ' selected') : (count + ' available')
            )
          ),
          h('span', { className: 'gsp-chevron' + (isOpen ? ' gsp-chevron--open' : '') },
            h('svg', { viewBox: '0 0 24 24', width: 18, height: 18, fill: 'none' },
              h('path', { d: 'M6 9l6 6 6-6', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' })
            )
          )
        )
      ),
      isOpen ? h('div', { className: 'gsp-card-body' }, (group.roles || []).map(renderRole)) : null
    );
  }

  return h('div', { className: 'gsp-overlay', onMouseDown: onCancel },
    h('div', { className: 'gsp-modal', onMouseDown: function(e) { e.stopPropagation(); } },

      // ---- left trust rail (verbatim copy) ----
      h('aside', { className: 'gsp-rail' },
        h('div', { className: 'gsp-rail-top' },
          h('div', { className: 'gsp-rail-brand' },
            h('span', { className: 'gsp-rail-shield' },
              h('svg', { viewBox: '0 0 24 24', width: 16, height: 16, fill: 'none' },
                h('path', { d: 'M12 3l7 3v5c0 4.4-3 8.4-7 9.5-4-1.1-7-5.1-7-9.5V6l7-3Z', stroke: 'currentColor', strokeWidth: 1.8, strokeLinejoin: 'round' })
              )
            ),
            h('span', null, 'Your data, protected')
          ),
          h('span', { className: 'gsp-rail-badge' },
            h('span', { className: 'gsp-rail-badge-dot' }),
            'Verification in progress'
          ),
          h('h3', { className: 'gsp-rail-title' }, 'You’re safe — the next screen just looks scary.'),
          h('p', { className: 'gsp-rail-copy' }, 'We’re still completing Google’s verification process, so Google shows an “unverified app” notice for now. It’s a routine step, not a security problem — thousands of trusted apps go through it. Your Google password is never shared with us, you choose exactly what to allow, and you can disconnect anytime. Here’s how to continue:'),
          h('ol', { className: 'gsp-steps' },
            h('li', { className: 'gsp-step' },
              h('span', { className: 'gsp-step-num' }, '1'),
              h('span', { className: 'gsp-step-text' },
                h('span', { className: 'gsp-step-title' }, h('strong', null, 'Click Advanced')),
                h('span', { className: 'gsp-step-sub' }, 'Bottom-left of the warning screen.')
              )
            ),
            h('li', { className: 'gsp-step' },
              h('span', { className: 'gsp-step-num' }, '2'),
              h('span', { className: 'gsp-step-text' },
                h('span', { className: 'gsp-step-title' }, 'Click ', h('strong', null, 'Go to Alleflow')),
                h('span', { className: 'gsp-step-sub' }, 'This just bypasses the unverified notice.')
              )
            ),
            h('li', { className: 'gsp-step gsp-step--done' },
              h('span', { className: 'gsp-step-num gsp-step-num--check' },
                h('svg', { viewBox: '0 0 24 24', width: 13, height: 13, fill: 'none' },
                  h('path', { d: 'M5 12l4 4 10-10', stroke: 'currentColor', strokeWidth: 2.4, strokeLinecap: 'round', strokeLinejoin: 'round' })
                )
              ),
              h('span', { className: 'gsp-step-text' },
                h('span', { className: 'gsp-step-title' }, h('strong', null, 'Approve the permissions')),
                h('span', { className: 'gsp-step-sub' }, 'You’re connected. Revoke anytime.')
              )
            )
          ),
          // Screenshot of the actual Google warning screen so it's instantly
          // recognizable when it appears. Served from app/public.
          h('img', {
            className: 'gsp-rail-shot',
            src: '/google_click_continue.png',
            alt: 'The Google “hasn’t verified this app” warning screen — click Advanced, bottom-left'
          })
        )
      ),

      // ---- right form panel ----
      h('section', { className: 'gsp-panel' },
        h('header', { className: 'gsp-panel-head' },
          h('span', { className: 'gsp-panel-icon' },
            h('svg', { viewBox: '0 0 24 24', width: 22, height: 22 },
              h('path', { d: 'M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z', fill: '#4285F4' }),
              h('path', { d: 'M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z', fill: '#34A853' }),
              h('path', { d: 'M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z', fill: '#FBBC05' }),
              h('path', { d: 'M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z', fill: '#EA4335' })
            )
          ),
          h('div', { className: 'gsp-panel-head-text' },
            h('h2', { className: 'gsp-panel-title' }, 'Connect a Google account'),
            h('p', { className: 'gsp-panel-sub' }, 'Choose the permissions this connection can use.')
          ),
          h('button', { type: 'button', className: 'gsp-close', onClick: onCancel, 'aria-label': 'Close' },
            h('svg', { viewBox: '0 0 24 24', width: 18, height: 18, fill: 'none' },
              h('path', { d: 'M6 6l12 12M18 6L6 18', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' })
            )
          )
        ),

        h('div', { className: 'gsp-panel-body' },
          loadError ? h('div', { className: 'gsp-error' }, loadError) : null,

          h('div', { className: 'gsp-section' },
            h('label', { className: 'gsp-section-label', htmlFor: 'gsp-name' }, 'Account name'),
            h('input', {
              id: 'gsp-name',
              className: 'gsp-name-input',
              type: 'text',
              value: name,
              autoFocus: true,
              placeholder: 'e.g. Work Gmail',
              onChange: function(e) { setName(e.target.value); },
              onKeyDown: function(e) { if (e.key === 'Enter') submit(); }
            }),
            (submitted && nameMissing) ? h('span', { className: 'gsp-hint-error' }, 'An account name is required.') : null
          ),

          h('div', { className: 'gsp-section' },
            h('div', { className: 'gsp-section-row' },
              h('span', { className: 'gsp-section-label' }, 'Permissions'),
              h('button', { type: 'button', className: 'gsp-selectall', onClick: toggleSelectAll },
                allOn ? 'Clear all' : 'Select all'
              )
            ),
            h('div', { className: 'gsp-cards' }, groups.map(renderGroup)),
            (submitted && noScopes) ? h('span', { className: 'gsp-hint-error' }, 'Select at least one permission.') : null
          )
        ),

        h('footer', { className: 'gsp-panel-foot' },
          h('span', { className: 'gsp-foot-status' },
            noScopes ? 'No permissions selected' : (selectedScopes.length + ' permission' + (selectedScopes.length === 1 ? '' : 's') + ' selected')
          ),
          h('div', { className: 'gsp-foot-actions' },
            h('button', { type: 'button', className: 'gsp-btn gsp-btn-cancel', onClick: onCancel }, 'Cancel'),
            h('button', {
              type: 'button',
              className: 'gsp-btn gsp-btn-connect',
              disabled: nameMissing || noScopes,
              onClick: submit
            }, 'Connect account')
          )
        )
      )
    )
  );
}

module.exports = GoogleScopePickerModal;
